import { type ClassValue, clsx } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

/**
 * Every theme token in the repo whose scale tailwind-merge cannot work out from
 * the class name alone.
 *
 * ## Why a registry is needed at all
 *
 * tailwind-merge decides which of two conflicting utilities survives by looking
 * up the scale each one reads. It works that out from the class name, and
 * manages it for numeric and arbitrary values (`px-4`, `px-[3px]`, `px-(--x)`)
 * and for the default scale names (`rounded-lg`, `text-xs`) — but nothing in
 * `px-content-safe-padding-sm` distinguishes a padding utility from some
 * plugin's unrelated `px-*`. An unrecognised token therefore conflicts with
 * nothing: a caller's `px-0` is appended beside it rather than replacing it,
 * both rules reach the element, and the cascade — not the caller — picks the
 * winner.
 *
 * That failure is invisible to every cheap check. The merged string still
 * literally contains `px-0`, so class assertions, markup diffs and snapshots all
 * agree with the caller while the page paints the other rule. It shows up only
 * as pixels: the rcprofi homepage banner and tiled menu are full-bleed via
 * `px-0 lap:px-0`, and both rendered inset by the gutter for as long as the
 * merger did not know the token.
 *
 * ## Why the registry is repo-wide, and where tenant tokens go
 *
 * A tenant's own tokens belong in THIS list, not in a merger of its own. The
 * merges that matter happen inside SHARED components — `Container` in
 * `@vs-shop/ui` merges whatever `classNameInner` its caller passes — and a
 * shared component imports one merger and cannot know which tenant is rendering
 * it. A per-tenant merger would leave `<Container classNameInner="px-rl-gutter">`
 * broken in exactly the way described above, because `Container` would still
 * merge through the shared one. So the registry is the union across
 * `@vs-shop/ui` and every tenant, and there is deliberately no factory for
 * building a second merger.
 *
 * Sharing one namespace across tenants is safe: tailwind-merge keys on a token's
 * NAME and never its value, so two brands may both define
 * `--spacing-content-safe-padding-lg` at different lengths and share one entry
 * here correctly.
 *
 * ## Maintaining it
 *
 * Registration is per SCALE, not per utility, mirroring how `@theme` declares
 * the token: the single `spacing` entry below teaches `px-*`, `mx-*`, `gap-*`,
 * `w-*`, `top-*` and every other utility reading that scale. Keys are the
 * `--<scale>` prefix of the custom property; values are the token names as
 * written in the stylesheet.
 *
 * A token added to any `@theme` block is added here in the same change.
 * `tools/guards/themeTokenRegistry.unit.test.ts` reads every stylesheet in
 * `packages/` and `apps/`, and fails naming the token and the file it came from.
 *
 * ## Scales deliberately absent
 *
 * - `--color-*` and `--font-*` need no entry whatever they are named:
 *   tailwind-merge matches those groups with an accept-any rule, so
 *   `bg-<anything>` and `font-<anything>` already conflict correctly.
 * - `--breakpoint-*` becomes a variant prefix (`lap:`), never a utility, so
 *   there is no conflict to resolve.
 * - `--radius-*` is name-matched and would need entries — the repo's tokens
 *   happen to reuse Tailwind's own names (`xs`…`2xl`), so they resolve already.
 *   A `--radius-pill` would not, and the guard catches it.
 */
export const themeTokens = {
  /**
   * `--spacing-*`. The `content-safe-padding-*` page gutter is `@vs-shop/ui`'s
   * (mirrored by rcprofi). Every other length in the repo is written as a
   * numeric utility off Tailwind's own `--spacing` scale, which needs no entry.
   */
  spacing: ['content-safe-padding-sm', 'content-safe-padding-lg'],
  /** `--tracking-*` beyond Tailwind's `tighter`…`widest`. `@vs-shop/ui`'s. */
  tracking: ['extra-wide'],
  /** `--shadow-*` beyond Tailwind's `2xs`…`2xl`. `@vs-shop/ui`'s. */
  shadow: ['elevation-low', 'elevation-medium', 'elevation-high'],
  /**
   * `--container-*` beyond Tailwind's `3xs`…`7xl`; powers `max-w-*` / `w-*`.
   * All tenant-declared: `cms-*` and `teamcard` are rcprofi's,
   * `prodcard-minwidth` is shared by rcprofi and racingline.
   */
  container: ['cms-base', 'cms-lg', 'prodcard-minwidth', 'teamcard'],
};

/**
 * Tokens whose utility tailwind-merge models as a plain class group rather than
 * a theme scale, so they are registered one class at a time.
 *
 * `z` has no theme scale to extend — `{ theme: { z: [...] } }` is accepted and
 * then ignored. The codebase writes `--z-max` as `z-(--z-max)`, which is a
 * custom-property value and needs no help; this covers the `z-max` spelling
 * Tailwind also generates for the same token.
 */
export const themeClassGroups = {
  z: ['z-max'],
  /**
   * The heading type scale in `@vs-shop/ui`: `text-h2` sets size, line-height
   * and weight together. It replaces all three, and a plain size such as
   * `text-lg` replaces it.
   */
  'text-style': [{ text: ['h1', 'h2', 'h3', 'h4'] }],
  animate: ['animate-cart-trace'],
};

const merge = extendTailwindMerge<keyof typeof themeClassGroups>({
  extend: {
    theme: themeTokens,
    classGroups: themeClassGroups,
    conflictingClassGroups: {
      'text-style': ['font-size', 'leading', 'font-weight'],
      'font-size': ['text-style'],
    },
  },
});

/**
 * The one class merger in this repo.
 *
 * Combines class values the way `clsx` does, then resolves Tailwind conflicts so
 * the last utility wins — for the tokens above as much as for `px-4`.
 *
 * Every package and every tenant imports THIS one, and there is deliberately no
 * factory for building another. A second merger is a second registry to keep in
 * step; it cannot help the shared components that do most of the merging, since
 * those import one merger and cannot know which tenant is rendering them; and
 * the symptom of it falling behind is an override that silently does nothing.
 * A token that needs resolving belongs in the registry above.
 */
export const clsxm = (...inputs: ClassValue[]): string => merge(clsx(inputs));
